import { useState, useCallback, useMemo } from 'react';
import { 
  Folder, ChevronRight, ChevronDown, Plus, Trash2, Edit3, X,
  FileCode, FileJson, FileText, FileType, FileImage, FileAudio, 
  FileVideo, FileArchive, FileSpreadsheet, FileHeart
} from 'lucide-react';
import { useEditorStore, EditorFile } from '../../stores/editorStore';
import { deleteFile, updateFile } from '../../services/editorApi';
import { fileSystemAccess } from '../../services/fileSystem';
import { toast } from 'sonner';

interface FileNode {
  id: string;
  name: string;
  path: string;
  type: 'file' | 'folder';
  children?: FileNode[];
  language?: string;
  content?: string;
  isLocal?: boolean;
}

interface FileTreeNodeProps {
  node: FileNode;
  level: number;
  onFileClick: (node: FileNode) => void;
  onContextMenu: (e: React.MouseEvent, node: FileNode) => void;
  selectedId: string | null;
  expandedFolders: Set<string>;
  onToggleFolder: (path: string) => void;
}

// Get file icon based on extension/language
function getFileIcon(language?: string, fileName?: string) {
  const ext = fileName?.split('.').pop()?.toLowerCase() || '';
  const lang = language?.toLowerCase() || ext;

  // Programming languages
  if (['typescript', 'typescriptreact', 'ts', 'tsx'].includes(lang) || ext === 'ts' || ext === 'tsx') {
    return { icon: FileCode, color: 'text-blue-500' };
  }
  if (['javascript', 'javascriptreact', 'js', 'jsx', 'mjs'].includes(lang) || ext === 'js' || ext === 'jsx') {
    return { icon: FileCode, color: 'text-yellow-500' };
  }
  if (['python', 'py'].includes(lang) || ext === 'py') {
    return { icon: FileCode, color: 'text-green-500' };
  }
  if (['csharp', 'c#', 'cs'].includes(lang) || ext === 'cs') {
    return { icon: FileCode, color: 'text-purple-500' };
  }
  if (['java'].includes(lang) || ext === 'java') {
    return { icon: FileCode, color: 'text-red-500' };
  }
  if (['c', 'cpp', 'h', 'hpp', 'cc', 'cxx'].includes(lang) || ['c', 'cpp', 'h', 'hpp'].includes(ext)) {
    return { icon: FileCode, color: 'text-blue-400' };
  }
  if (['go', 'golang'].includes(lang) || ext === 'go') {
    return { icon: FileCode, color: 'text-cyan-500' };
  }
  if (['rust', 'rs'].includes(lang) || ext === 'rs') {
    return { icon: FileCode, color: 'text-orange-500' };
  }
  if (['swift', 'kt', 'kotlin'].includes(lang) || ['swift', 'kt'].includes(ext)) {
    return { icon: FileCode, color: 'text-orange-400' };
  }
  if (['php'].includes(lang) || ext === 'php') {
    return { icon: FileCode, color: 'text-indigo-500' };
  }
  if (['ruby', 'rb'].includes(lang) || ext === 'rb') {
    return { icon: FileCode, color: 'text-red-400' };
  }

  // Web files
  if (['html', 'htm'].includes(lang) || ['html', 'htm'].includes(ext)) {
    return { icon: FileCode, color: 'text-orange-500' };
  }
  if (['css', 'scss', 'sass', 'less'].includes(lang) || ['css', 'scss', 'sass', 'less'].includes(ext)) {
    return { icon: FileCode, color: 'text-blue-500' };
  }

  // Data files
  if (['json'].includes(lang) || ext === 'json') {
    return { icon: FileJson, color: 'text-yellow-600' };
  }
  if (['xml'].includes(lang) || ext === 'xml') {
    return { icon: FileCode, color: 'text-blue-400' };
  }
  if (['yaml', 'yml'].includes(lang) || ['yaml', 'yml'].includes(ext)) {
    return { icon: FileCode, color: 'text-green-600' };
  }
  if (['toml', 'ini', 'conf', 'config'].includes(lang) || ['toml', 'ini', 'conf'].includes(ext)) {
    return { icon: FileType, color: 'text-gray-500' };
  }

  // Documents
  if (['markdown', 'md', 'mdx'].includes(lang) || ['md', 'mdx'].includes(ext)) {
    return { icon: FileText, color: 'text-gray-600' };
  }
  if (['text', 'txt', 'plaintext'].includes(lang) || ['txt'].includes(ext)) {
    return { icon: FileText, color: 'text-gray-500' };
  }
  if (['pdf'].includes(lang) || ext === 'pdf') {
    return { icon: FileText, color: 'text-red-500' };
  }
  if (['doc', 'docx'].includes(lang) || ['doc', 'docx'].includes(ext)) {
    return { icon: FileText, color: 'text-blue-600' };
  }

  // Images
  if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'ico', 'webp', 'bmp'].includes(ext)) {
    return { icon: FileImage, color: 'text-purple-500' };
  }

  // Audio
  if (['mp3', 'wav', 'ogg', 'flac', 'aac'].includes(ext)) {
    return { icon: FileAudio, color: 'text-green-500' };
  }

  // Video
  if (['mp4', 'avi', 'mov', 'wmv', 'webm'].includes(ext)) {
    return { icon: FileVideo, color: 'text-red-500' };
  }

  // Archives
  if (['zip', 'tar', 'gz', 'rar', '7z'].includes(ext)) {
    return { icon: FileArchive, color: 'text-yellow-600' };
  }

  // Spreadsheets
  if (['csv', 'xls', 'xlsx'].includes(ext)) {
    return { icon: FileSpreadsheet, color: 'text-green-600' };
  }

  // Default
  return { icon: FileType, color: 'text-gray-500' };
}

function FileTreeNode({ node, level, onFileClick, onContextMenu, selectedId, expandedFolders, onToggleFolder }: FileTreeNodeProps) {
  const isFolder = node.type === 'folder';
  const isSelected = selectedId === node.id;
  const isExpanded = expandedFolders.has(node.path);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isFolder) {
      onToggleFolder(node.path);
    } else {
      onFileClick(node);
    }
  }, [isFolder, node, onFileClick, onToggleFolder]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onContextMenu(e, node);
  }, [node, onContextMenu]);

  const { icon: FileIcon, color } = isFolder 
    ? { icon: Folder, color: 'text-yellow-500' }
    : getFileIcon(node.language, node.name);

  return (
    <div>
      <div
        className={`flex items-center px-2 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
          isSelected ? 'bg-blue-100 dark:bg-blue-900' : ''
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      >
        {isFolder && (
          <span className="mr-1">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </span>
        )}
        {!isFolder && <span className="mr-1 w-4" />}
        <FileIcon className={`w-4 h-4 mr-2 ${color}`} />
        <span className="text-sm truncate">{node.name}</span>
      </div>
      {isFolder && isExpanded && node.children && (
        <div>
          {node.children.map((child) => (
            <FileTreeNode
              key={child.id}
              node={child}
              level={level + 1}
              onFileClick={onFileClick}
              onContextMenu={onContextMenu}
              selectedId={selectedId}
              expandedFolders={expandedFolders}
              onToggleFolder={onToggleFolder}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface FileExplorerProps {
  onFileSelect?: (file: EditorFile) => void;
  onNewFile?: (isFolder: boolean) => void;
}

export function FileExplorer({ onFileSelect, onNewFile }: FileExplorerProps) {
  const { 
    currentProject, 
    files, 
    openFile, 
    setFiles,
    closeFile 
  } = useEditorStore();
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; node: FileNode | null } | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [renameNode, setRenameNode] = useState<FileNode | null>(null);
  const [newName, setNewName] = useState('');

  // Toggle folder expansion
  const toggleFolder = useCallback((path: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  }, []);

  // Build file tree from flat file list
  const buildFileTree = useCallback((): FileNode[] => {
    if (!files.length) return [];

    // Filter out system folders and files inside them
    const systemFolders = ['__pycache__', '__fixtures__', '__snapshots__', '.git', 'node_modules', 'root'];
    
    const filteredFiles = files.filter((file) => {
      // Check if file name is a system folder
      const name = file.name.toLowerCase();
      if (systemFolders.includes(name) || name.startsWith('.')) {
        return false;
      }
      
      // Check if file path contains any system folder
      const path = file.path.toLowerCase();
      for (const folder of systemFolders) {
        if (path.includes(`/${folder}/`) || path.endsWith(`/${folder}`) || path === `/${folder}` || path === folder) {
          return false;
        }
      }
      
      return true;
    });

    if (!filteredFiles.length) return [];

    // Use a tree structure with path-based lookup
    const tree: { [key: string]: FileNode } = {};
    const roots: FileNode[] = [];

    // Create all file nodes first
    filteredFiles.forEach((file) => {
      // Determine type based on file_type from backend or path for local files
      // Handle both uppercase (FOLDER) and lowercase (folder) from backend
      const fileType = (file as any).file_type?.toLowerCase();
      const isFolder = file.id.startsWith('local-') 
        ? false // Local files are always files, folders are created from path segments
        : fileType === 'folder';
      
      const node: FileNode = {
        id: file.id,
        name: file.name,
        path: file.path,
        type: isFolder ? 'folder' : 'file',
        children: [],
        language: file.language,
        content: file.content,
        isLocal: file.id.startsWith('local-'),
      };
      // Use the file's path as key (keep original format)
      tree[file.path] = node;
    });

    // Create folder nodes and build hierarchy
    filteredFiles.forEach((file) => {
      // Normalize path - remove leading slash for processing
      const normalizedPath = file.path.startsWith('/') ? file.path.slice(1) : file.path;
      const parts = normalizedPath.split('/');
      
      // Build folder hierarchy for this file's path
      let currentPath = '';
      for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];
        const parentPath = currentPath ? `${currentPath}/${part}` : part;
        
        // Skip empty paths or root paths
        if (!parentPath || parentPath === '/') continue;
        
        // Use normalized path (no trailing slash) as key for lookup
        if (!tree[parentPath]) {
          tree[parentPath] = {
            id: `folder-${parentPath}`,
            name: part,
            path: parentPath + '/',
            type: 'folder',
            children: [],
            isLocal: file.id.startsWith('local-'),
          };
        }
        currentPath = parentPath;
      }
    });

    // Build parent-child relationships
    Object.values(tree).forEach(node => {
      if (node.type === 'file') {
        // Find parent folder - normalize path
        const normalizedPath = node.path.startsWith('/') ? node.path.slice(1) : node.path;
        const lastSlash = normalizedPath.lastIndexOf('/');
        
        if (lastSlash === -1) {
          // Root level file (no parent folder)
          roots.push(node);
        } else {
          const parentPath = normalizedPath.substring(0, lastSlash);
          const parent = tree[parentPath];
          if (parent && parent.children) {
            parent.children.push(node);
          } else {
            // Parent not found, add to roots
            roots.push(node);
          }
        }
      } else if (node.type === 'folder') {
        // Find parent folder for folders
        const normalizedPath = node.path.startsWith('/') ? node.path.slice(1) : node.path;
        // Remove trailing slash
        const cleanPath = normalizedPath.endsWith('/') ? normalizedPath.slice(0, -1) : normalizedPath;
        const lastSlash = cleanPath.lastIndexOf('/');
        
        if (lastSlash === -1) {
          // Root level folder
          if (!roots.includes(node)) {
            roots.push(node);
          }
        } else {
          const parentPath = cleanPath.substring(0, lastSlash);
          const parent = tree[parentPath];
          if (parent && parent.children && !parent.children.includes(node)) {
            parent.children.push(node);
          } else if (!roots.includes(node)) {
            roots.push(node);
          }
        }
      }
    });

    // Sort children
    const sortChildren = (nodes: FileNode[]) => {
      nodes.sort((a, b) => {
        if (a.type === 'folder' && b.type !== 'folder') return -1;
        if (a.type !== 'folder' && b.type === 'folder') return 1;
        return a.name.localeCompare(b.name);
      });
      nodes.forEach(node => {
        if (node.children) {
          sortChildren(node.children);
        }
      });
    };

    sortChildren(roots);
    
    // Sort children of all folders
    Object.values(tree).forEach(node => {
      if (node.type === 'folder' && node.children) {
        sortChildren(node.children);
      }
    });

    return roots;
  }, [files]);

  const fileTree = useMemo(() => buildFileTree(), [buildFileTree]);

  const handleFileClick = useCallback(async (node: FileNode) => {
    if (node.type === 'file') {
      // Check if this is a local file that needs content loaded
      if (node.isLocal && !node.content) {
        try {
          // Read content from local file system
          const content = await fileSystemAccess.readFile(node.path);
          // Update the file in store with content
          const updatedFiles = files.map(f => 
            f.id === node.id ? { ...f, content } : f
          );
          setFiles(updatedFiles);
          // Open the file with loaded content
          const fileWithContent = { ...node, content, isLocal: true };
          openFile(fileWithContent as unknown as EditorFile);
          onFileSelect?.(fileWithContent as unknown as EditorFile);
        } catch (error) {
          console.error('Failed to read local file:', error);
          toast.error('Failed to read file content');
        }
      } else {
        const file = files.find(f => f.id === node.id);
        if (file) {
          openFile(file);
          onFileSelect?.(file);
        }
      }
    }
  }, [files, openFile, onFileSelect, setFiles]);

  const handleContextMenu = useCallback((e: React.MouseEvent, node: FileNode) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      node,
    });
  }, []);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleRename = useCallback(async () => {
    if (!renameNode || !newName.trim() || newName === renameNode.name) {
      setRenameNode(null);
      setNewName('');
      return;
    }

    try {
      if (renameNode.isLocal) {
        // Local file rename - not yet implemented
        toast.success('File renamed (local)');
      } else {
        // Online file rename - use API update
        // Calculate new path by replacing the old name with the new name
        const oldPath = renameNode.path;
        const oldName = renameNode.name;
        const newNameTrimmed = newName.trim();
        const newPath = oldPath.endsWith(oldName) 
          ? oldPath.slice(0, -oldName.length) + newNameTrimmed 
          : oldPath;
        
        await updateFile(renameNode.id, { name: newNameTrimmed, path: newPath });
        
        // Update the store with new name and path
        const updatedFiles = files.map(f => 
          f.id === renameNode.id ? { ...f, name: newNameTrimmed, path: newPath } : f
        );
        setFiles(updatedFiles);
        
        toast.success('File renamed');
      }
      
      setRenameNode(null);
      setNewName('');
    } catch (error) {
      console.error('Failed to rename file:', error);
      toast.error('Failed to rename file');
    }
  }, [renameNode, newName, files, setFiles]);

  const handleDelete = useCallback(async () => {
    if (!contextMenu?.node) return;

    const node = contextMenu.node;
    
    try {
      if (node.isLocal) {
        // Local file delete - use File System Access API
        const success = await fileSystemAccess.deleteFile(node.path);
        if (success) {
          // Remove from store
          const updatedFiles = files.filter(f => f.id !== node.id);
          setFiles(updatedFiles);
          // Close if open
          closeFile(node.id);
          toast.success('File deleted');
        } else {
          toast.error('File not found');
        }
      } else {
        // Online file delete - use API
        await deleteFile(node.id);
        // Remove from store
        const updatedFiles = files.filter(f => f.id !== node.id);
        setFiles(updatedFiles);
        // Close if open
        closeFile(node.id);
        toast.success('File deleted');
      }
    } catch (error) {
      console.error('Failed to delete file:', error);
      toast.error('Failed to delete file: ' + (error as any).message);
    }
    
    setContextMenu(null);
  }, [contextMenu, files, setFiles, closeFile]);

  if (!currentProject) {
    return (
      <div className="p-4 text-sm text-gray-500">
        <p>No project selected</p>
        <p className="mt-2">Create or open a project to start editing.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" onClick={handleCloseContextMenu}>
      <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase text-gray-500">
            {currentProject.name}
          </span>
          <div className="flex gap-1">
            <button
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              onClick={() => onNewFile?.(false)}
              title="New File"
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              onClick={() => onNewFile?.(true)}
              title="New Folder"
            >
              <Folder className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      
      <div className="flex-1 overflow-auto">
        {fileTree.map((node) => (
          <FileTreeNode
            key={node.id}
            node={node}
            level={0}
            onFileClick={handleFileClick}
            onContextMenu={handleContextMenu}
            selectedId={null}
            expandedFolders={expandedFolders}
            onToggleFolder={toggleFolder}
          />
        ))}
        
        {fileTree.length === 0 && (
          <div className="p-4 text-sm text-gray-500">
            <p>No files yet</p>
            <p className="mt-1">Create your first file to get started.</p>
          </div>
        )}
      </div>

      {/* Rename Modal */}
      {renameNode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 min-w-80">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Rename File</h3>
              <button
                onClick={() => { setRenameNode(null); setNewName(''); }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
              placeholder="Enter new name"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRename();
                if (e.key === 'Escape') { setRenameNode(null); setNewName(''); }
              }}
            />
            <div className="flex gap-2 mt-4 justify-end">
              <button
                onClick={() => { setRenameNode(null); setNewName(''); }}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleRename}
                className="px-4 py-2 text-sm bg-blue-500 text-white hover:bg-blue-600 rounded"
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg py-1"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          {contextMenu.node?.type === 'folder' && (
            <>
              <button
                className="w-full px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                onClick={() => {
                  // Add file in this folder
                  onNewFile?.(false);
                  setContextMenu(null);
                }}
              >
                <Plus className="w-4 h-4" />
                Add File
              </button>
              <button
                className="w-full px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                onClick={() => {
                  // Add folder in this folder
                  onNewFile?.(true);
                  setContextMenu(null);
                }}
              >
                <Folder className="w-4 h-4" />
                Add Folder
              </button>
              <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
            </>
          )}
          <button
            className="w-full px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            onClick={() => {
              setRenameNode(contextMenu.node);
              setNewName(contextMenu.node?.name || '');
              setContextMenu(null);
            }}
          >
            <Edit3 className="w-4 h-4" />
            Rename
          </button>
          <button
            className="w-full px-3 py-2 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-red-600"
            onClick={handleDelete}
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}